import {
  GaussianBlurBackgroundProcessor,
  ImageFit,
  VirtualBackgroundProcessor,
  isSupported,
} from "@twilio/video-processors";
import type { LocalVideoTrack, VideoProcessor } from "twilio-video";
import { logger } from "../../../../datadog/logger";
import { getBackgroundImageUrl } from "../../../core/backgrounds";
import { BackgroundType } from "../../../core/types";
import { getStoredAudioVideoSettings } from "../../../core/utils";

export const WEBGL_EXECUTION_ERROR =
  "Failed to execute 'clientWaitSync' on 'WebGL2RenderingContext'";

// corresponds to a generated directory in the public folder
const virtualBackgroundAssets = "/virtualbackground";

export let blurProcessor: GaussianBlurBackgroundProcessor | undefined =
  undefined;
export let virtualBackgroundProcessor: VirtualBackgroundProcessor | undefined =
  undefined;

export function removeProcessor(videoTrack: LocalVideoTrack) {
  if (videoTrack && videoTrack.processor) {
    videoTrack.removeProcessor(videoTrack.processor);
  }
}

export function addProcessor(
  videoTrack: LocalVideoTrack,
  processor:
    | GaussianBlurBackgroundProcessor
    | VirtualBackgroundProcessor
    | VideoProcessor,
) {
  if (videoTrack && videoTrack.processor !== processor) {
    removeProcessor(videoTrack);
    videoTrack.addProcessor(processor, {
      inputFrameBufferType: "videoframe",
      outputFrameBufferContextType: "bitmaprenderer",
    });
  }
}

/**
 * Initializes and loads background processor models if not already loaded.
 */
export async function loadProcessorModels() {
  if (!isSupported) return;

  if (!blurProcessor) {
    blurProcessor = new GaussianBlurBackgroundProcessor({
      blurFilterRadius: 15,
      assetsPath: virtualBackgroundAssets,
    });
    try {
      await blurProcessor.loadModel();
    } catch (error) {
      logger.error("Unable to load blur model", {}, error as Error);
    }
  }
  if (!virtualBackgroundProcessor) {
    virtualBackgroundProcessor = new VirtualBackgroundProcessor({
      maskBlurRadius: 5,
      assetsPath: virtualBackgroundAssets,
      backgroundImage: await getBackgroundImageUrl(0),
      fitType: ImageFit.Cover,
    });
    try {
      await virtualBackgroundProcessor.loadModel();
    } catch (error) {
      logger.error(
        "Unable to load virtual background model",
        {},
        error as Error,
      );
    }
  }
}

/**
 * Returns the current background processor based on the stored audio video settings.
 *
 * Assumes the processor is already initialized.
 */
export function getCurrentProcessor():
  | VirtualBackgroundProcessor
  | GaussianBlurBackgroundProcessor
  | undefined {
  const storedAudioVideoSettings = getStoredAudioVideoSettings();
  const backgroundType = storedAudioVideoSettings.backgroundType;
  switch (backgroundType) {
    case BackgroundType.BLUR:
      return blurProcessor;
    case BackgroundType.IMAGE:
      return virtualBackgroundProcessor;
    default:
      return;
  }
}
